'use strict'

/**
 * @param {Record<string, unknown> | null | undefined} s
 * @returns {{
 *   total: number
 *   updated: number
 *   updatePending: number
 *   reupdatePending: number
 *   verificationPending: number
 *   verifying: number
 *   error: number
 *   configuring: number
 *   unset: number
 * }}
 */
function mapUpdateStatus(s) {
  if (!s || typeof s !== 'object') {
    return {
      total: 0,
      updated: 0,
      updatePending: 0,
      reupdatePending: 0,
      verificationPending: 0,
      verifying: 0,
      error: 0,
      configuring: 0,
      unset: 0
    }
  }
  return {
    total: s.total ?? 0,
    updated: s.updated ?? 0,
    updatePending: s.update_pending ?? 0,
    reupdatePending: s.reupdate_pending ?? 0,
    verificationPending: s.verification_pending ?? 0,
    verifying: s.verifying ?? 0,
    error: s.error ?? 0,
    configuring: s.configuring ?? 0,
    unset: s.unset ?? 0
  }
}

/** @param {Record<string, unknown>} t */
function mapTask(t) {
  return {
    key: t.key,
    periodKind: t.period_kind,
    periodValue: t.period_value,
    displayStatus: t.display_status,
    message: t.message ?? '',
    personnelUpdateStatus: mapUpdateStatus(t.personnel_update_status),
    accountingUpdateStatus: mapUpdateStatus(t.accounting_update_status)
  }
}

/**
 * IF02098 API ボディを画面用 camelCase に正規化する
 *
 * @param {object} body - ParseDataUpdateTaskInfoApiBody の戻り値
 * @returns {{
 *   fiscalPeriodOptions: string[]
 *   fiscalPeriodGroups: { fiscalPeriod: string; tasks: ReturnType<typeof mapTask>[] }[]
 * }}
 */
export function mapDataUpdateTaskInfoToViewModel(body) {
  const fiscalPeriodOptions = body.fiscal_period_options ?? []
  const fiscalPeriodGroups = (body.fiscal_period_groups ?? []).map((g) => ({
    fiscalPeriod: g.fiscal_period,
    tasks: (g.tasks ?? []).map(mapTask)
  }))
  return { fiscalPeriodOptions, fiscalPeriodGroups }
}

/**
 * 更新状況オブジェクトの「更新待ち＋再更新待ち」件数を合計する
 *
 * @param {Record<string, number> | null | undefined} status - MapUpdateStatus 済みの更新状況
 * @returns {number} 更新待ち＋再更新待ちの合計件数
 */
export function sumWaitingAndPendingReupdate(status) {
  return (status?.updatePending ?? 0) + (status?.reupdatePending ?? 0)
}

/**
 * タスクの人事・財務を合算した「更新待ち＋再更新待ち」件数を返す
 *
 * @param {ReturnType<typeof mapTask>} task - ビューモデルのタスク
 * @returns {number} 人事・財務合算の更新待ち＋再更新待ち件数
 */
export function taskCombinedUpdateWaitCount(task) {
  return (
    sumWaitingAndPendingReupdate(task.personnelUpdateStatus) + sumWaitingAndPendingReupdate(task.accountingUpdateStatus)
  )
}

/**
 * タスクが「更新待ち」通知の対象かを判定する。 データ分類の更新待ち・再更新待ちが1件以上あるタスク、またはタスクの表示ステータスが 再更新待ち（過去期間の更新による波及）のタスクを対象とする。
 * タスク一覧ヘッダの「更新待ち〇件」「再更新待ち〇件」と同一基準。
 *
 * @param {ReturnType<typeof mapTask>} task - ビューモデルのタスク
 * @returns {boolean} 更新待ち通知の対象なら true
 */
export function isUpdateWaitingTask(task) {
  return taskCombinedUpdateWaitCount(task) > 0 || task.displayStatus === 'pending_reupdate'
}
